import * as React from 'react';
import {
  Tournament,
  TournamentCompetitionDefinition,
  TournamentCompetitionRuntime,
  TournamentCustomGroup
} from 'Src/models/tournament';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import './TournamentStructureWorkspace.scss';

export type StructureTab = 'structure' | 'matchday' | 'public';

export function schoolInitials(name: string): string {
  const words = name
    .split(/\s+/)
    .filter(word => word && !/^(school|prep|college|junior|high|preparatory|the)$/i.test(word));
  return (
    (words.length ? words : name.split(/\s+/))
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase() || '?'
  );
}

export const SchoolMark: React.FC<{ name: string; pic?: string }> = ({ name, pic }) =>
  pic ? (
    <img className="tswSchoolMark" src={pic} alt="" />
  ) : (
    <span className="tswSchoolMark tswSchoolMark--initials" aria-hidden="true">
      {schoolInitials(name)}
    </span>
  );

interface Props {
  tournament: Tournament;
  definition: TournamentCompetitionDefinition;
  runtime?: TournamentCompetitionRuntime;
  groups: TournamentCustomGroup[];
  published: boolean;
  dirty: boolean;
  tab: StructureTab;
  onTab: (tab: StructureTab) => void;
  selectedGroupId?: string;
  onGroup: (id: string) => void;
  onMoveGroup?: (id: string, direction: -1 | 1) => void;
  onBack: () => void;
  rules: React.ReactNode;
  builder: React.ReactNode;
  schedule?: React.ReactNode;
  matchday: React.ReactNode;
  publicPage: React.ReactNode;
  footer: React.ReactNode;
  overlays: React.ReactNode;
  groupBoard?: (stageId: string, chooseCompetition: (id: string) => void) => React.ReactNode;
  onAddStage?: () => void;
  pendingGroupIds?: string[];
  onRefresh?: () => void;
  busy?: boolean;
}

export const TournamentStructureWorkspace: React.FC<Props> = props => {
  const { tournament, definition, runtime, groups, tab } = props;
  const [query, setQuery] = React.useState('');
  const [scheduleOpen, setScheduleOpen] = React.useState(false);
  const [scheduleVisited, setScheduleVisited] = React.useState(false);
  const [stageId, setStageId] = React.useState<string>();
  const [closed, setClosed] = React.useState<Record<string, boolean>>({});
  const [needsAction, setNeedsAction] = React.useState(true);
  React.useEffect(() => {
    if (runtime && !Object.keys(runtime.groups).length) setNeedsAction(false);
  }, [runtime ? Object.keys(runtime.groups).length : undefined]);
  const stages = [...(tournament.tournamentLayout || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const stageGroups = (id: string) => groups.filter(group => group.stageId === id);
  const selectedGroup = groups.find(group => group.id === props.selectedGroupId);
  const selectedStage = stages.find(stage => stage.id === (stageId || selectedGroup?.stageId || stages[0]?.id));
  React.useEffect(() => {
    if (stageId && !stages.some(stage => stage.id === stageId)) setStageId(stages[0]?.id);
  }, [stageId, stages.map(stage => stage.id).join(':')]);
  const actionCount = Object.keys(runtime?.groups || {}).filter(id => {
    const group = runtime!.groups[id];
    return group.isComplete && (!group.isConfirmed || props.pendingGroupIds?.includes(id));
  }).length;
  const isRoundRobin = (id: string) =>
    stages.find(stage => stage.id === id)?.format === 'ROUND_ROBIN' ||
    stages.find(stage => stage.id === id)?.type === 'GROUP' ||
    stageGroups(id).some(group => definition.qualifierGroups?.some(pool => pool.groupId === group.id));
  const chooseGroup = (id: string) => {
    const group = groups.find(item => item.id === id);
    setStageId(undefined);
    if (group) setClosed(current => ({ ...current, [group.stageId]: false }));
    props.onGroup(id);
  };
  const teamIdentity = (id: string) => {
    const team = runtime?.teams.find(item => item.id === id);
    const school = tournament.schools?.find(item => item.id === team?.schoolId);
    const schoolName = school?.name || team?.name || 'Tournament team';
    const multiple = runtime?.teams.filter(item => item.schoolId === team?.schoolId).length > 1;
    return { name: `${schoolName}${multiple && team?.name ? ` · ${team.name}` : ''}`, schoolName, pic: school?.pic };
  };
  const initialBoard = selectedStage && (
    <div className="tswStageBoard">
      <header>
        <div>
          <span className="tswEyebrow">Stage settings</span>
          <h2>{selectedStage.name}</h2>
          <p>
            {isRoundRobin(selectedStage.id) ? 'Round robin' : 'Knockout'} · {stageGroups(selectedStage.id).length}{' '}
            competitions
          </p>
        </div>
      </header>
      {props.groupBoard?.(selectedStage.id, chooseGroup) || (
        <div className="tswCompetitionCards">
          {stageGroups(selectedStage.id).map(group => {
            const teams = runtime?.teams.filter(team => team.groupIds.includes(group.id)) || [];
            const pool = definition.qualifierGroups?.find(item => item.groupId === group.id);
            return (
              <article key={group.id}>
                <button type="button" onClick={() => chooseGroup(group.id)}>
                  <strong>{group.name}</strong>
                  <span>
                    {pool ? pool.sources.length : teams.length} entrants ·{' '}
                    {definition.matches.filter(match => match.groupId === group.id).length} planned matches
                  </span>
                </button>
                <ul>
                  {teams.map(team => {
                    const identity = teamIdentity(team.id);
                    return (
                      <li key={team.id}>
                        <SchoolMark name={identity.schoolName} pic={identity.pic} />
                        <span>{identity.name}</span>
                      </li>
                    );
                  })}
                </ul>
              </article>
            );
          })}
        </div>
      )}
      {!stageGroups(selectedStage.id).length && <p className="tswEmpty">No competitions in this stage yet.</p>}
    </div>
  );
  return (
    <main className={`tcsPage tsw tsw--${tab}`}>
      <header className="tswHero">
        <div className="tswHero__inner">
          <button type="button" className="tswBack" onClick={props.onBack}>
            ← Back to competition
          </button>
          <div className="tswHero__heading">
            <div>
              <div className="tswTags">
                <span>{tournament.sport?.name || 'Team tournament'}</span>
                <span>
                  {[
                    formatTournamentAges(tournament.ages),
                    TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[tournament.gender] || tournament.gender
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Competition structure'}
                </span>
                <span className={props.published ? (props.dirty ? 'is-warning' : 'is-success') : 'is-neutral'}>
                  {props.published ? (props.dirty ? 'Published · unpublished changes' : 'Published') : 'Draft'}
                </span>
              </div>
              <h1>{tournament.name}</h1>
            </div>
            <dl>
              {[
                [stages.length, 'stages'],
                [groups.length, 'competitions'],
                [definition.matches.filter(match => !match.removed).length, 'planned matches']
              ].map(([count, label]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{count}</dd>
                </div>
              ))}
            </dl>
          </div>
          <nav className="tswTabs" aria-label="Tournament structure">
            {([
              { id: 'structure', label: 'Structure' },
              { id: 'matchday', label: 'Matchday' },
              { id: 'public', label: 'Public page' }
            ] as const).map(item => (
              <button
                type="button"
                key={item.id}
                aria-current={tab === item.id ? 'page' : undefined}
                onClick={() => props.onTab(item.id)}
              >
                {item.label}
                {item.id === 'matchday' && actionCount > 0 && <span>{actionCount}</span>}
              </button>
            ))}
          </nav>
        </div>
      </header>
      <fieldset className="tswBody" disabled={props.busy}>
        {tab === 'structure' && props.schedule && (
          <div className="tswSegment">
            <button type="button" aria-pressed={!scheduleOpen} onClick={() => setScheduleOpen(false)}>Competitions</button>
            <button type="button" aria-pressed={scheduleOpen} onClick={() => {
              setScheduleVisited(true);
              setScheduleOpen(true);
            }}>Schedule grid</button>
          </div>
        )}
        {scheduleVisited && <div hidden={tab !== 'structure' || !scheduleOpen}>{props.schedule}</div>}
        {tab === 'structure' && !scheduleOpen && (
          <>
            <div className="tswRules">{props.rules}</div>
            <div className="tswLayout">
              <aside className="tswTree" aria-label="Stages and competitions">
                <header>
                  <h2>Stages</h2>
                  <div>
                    <button type="button" onClick={() => setClosed({})}>
                      Open all
                    </button>
                    <button
                      type="button"
                      onClick={() => setClosed(stages.reduce((result, stage) => ({ ...result, [stage.id]: true }), {}))}
                    >
                      Close all
                    </button>
                  </div>
                </header>
                <input
                  type="search"
                  aria-label="Search stages and competitions"
                  placeholder="Find a stage or competition"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                />
                {stages
                  .filter(
                    stage =>
                      !query ||
                      `${stage.name} ${stageGroups(stage.id)
                        .map(group => group.name)
                        .join(' ')}`
                        .toLowerCase()
                        .includes(query.toLowerCase())
                  )
                  .map((stage, index) => (
                    <section key={stage.id}>
                      <div className="tswTree__stage">
                        <button
                          type="button"
                          aria-label={`${closed[stage.id] ? 'Expand' : 'Collapse'} ${stage.name}`}
                          aria-expanded={!closed[stage.id]}
                          onClick={() => setClosed(current => ({ ...current, [stage.id]: !current[stage.id] }))}
                        >
                          {closed[stage.id] ? '›' : '⌄'}
                        </button>
                        <button
                          type="button"
                          className={stageId === stage.id ? 'is-selected' : ''}
                          onClick={() => {
                            setStageId(stage.id);
                            setClosed(current => ({ ...current, [stage.id]: false }));
                          }}
                        >
                          <small>
                            Stage {index + 1} · {isRoundRobin(stage.id) ? 'Round robin' : 'Knockout'}
                          </small>
                          <strong>{stage.name}</strong>
                        </button>
                      </div>
                      {!closed[stage.id] &&
                        stageGroups(stage.id)
                          .filter(
                            group =>
                              !query ||
                              stage.name.toLowerCase().includes(query.toLowerCase()) ||
                              group.name.toLowerCase().includes(query.toLowerCase())
                          )
                          .map(group => {
                            const siblings = stageGroups(stage.id);
                            const position = siblings.findIndex(item => item.id === group.id);
                            return (
                              <div className="tswTree__competitionRow" key={group.id}>
                                <button
                                  type="button"
                                  className={`tswTree__competition ${
                                    !stageId && selectedGroup?.id === group.id ? 'is-selected' : ''
                                  }`}
                                  onClick={() => chooseGroup(group.id)}
                                >
                                  <span>{group.name}</span>
                                  <small>{definition.matches.filter(match => match.groupId === group.id).length}</small>
                                </button>
                                {props.onMoveGroup && siblings.length > 1 && (
                                  <div className="tswTree__order">
                                    <button
                                      type="button"
                                      aria-label={`Move ${group.name} up`}
                                      disabled={position === 0}
                                      onClick={() => props.onMoveGroup?.(group.id, -1)}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      type="button"
                                      aria-label={`Move ${group.name} down`}
                                      disabled={position === siblings.length - 1}
                                      onClick={() => props.onMoveGroup?.(group.id, 1)}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                    </section>
                  ))}
                {props.onAddStage && (
                  <button className="tswAddStage" type="button" onClick={props.onAddStage}>
                    + Add stage
                  </button>
                )}
              </aside>
              <div className="tswEditor">
                {stageId || (selectedStage?.type === 'GROUP' && !selectedGroup) ? initialBoard : props.builder}
              </div>
            </div>
          </>
        )}
        {tab === 'matchday' && (
          <div className={`tswMatchday ${needsAction ? 'tswMatchday--needs-action' : ''}`}>
            <header className="tswSectionHeading">
              <div>
                <h2>Matchday</h2>
                <p>
                  Review and confirm each group before its finishing places create the next fixtures. These actions
                  apply live.
                </p>
              </div>
              <div className="tswSegment">
                <button type="button" aria-pressed={needsAction} onClick={() => setNeedsAction(true)}>
                  Needs action {actionCount > 0 && `(${actionCount})`}
                </button>
                <button type="button" aria-pressed={!needsAction} onClick={() => setNeedsAction(false)}>
                  All
                </button>
                {props.onRefresh && (
                  <button type="button" onClick={props.onRefresh}>
                    Refresh
                  </button>
                )}
              </div>
            </header>
            {props.matchday}
            {needsAction && actionCount === 0 && <p className="tswEmpty">No completed groups need confirmation.</p>}
          </div>
        )}
        {tab === 'public' && (
          <div className="tswPublic">
            <header className="tswSectionHeading">
              <div>
                <h2>Public page</h2>
                <p>Control the rules and progression information shown to schools and families.</p>
              </div>
            </header>
            {props.publicPage}
          </div>
        )}
      </fieldset>
      {tab !== 'matchday' && (
        <div className="tswFooter">
          <span>
            {props.dirty
              ? 'Unpublished changes · save a draft or publish when ready'
              : props.published
              ? 'All changes published'
              : 'Saved as draft until you publish'}
          </span>
          {props.footer}
        </div>
      )}
      {props.overlays}
    </main>
  );
};
